// ============================================================
// AuditTemplateBuilder.js
// The full section/question structure builder for platform audit
// templates, mounted from PlatformAuditTemplatesPage in a wide
// shared <Modal>. Edits the whole tree in local state (loaded from
// getPlatformAuditTemplate) and saves it in one shot via
// putPlatformAuditTemplateStructure.
//
// Behaviour decisions (stated here per the build brief):
//   - On a successful save the builder STAYS OPEN and reloads the
//     template from the server, so the operator sees the stored
//     state (fresh ids / sort orders) and can keep editing; the
//     parent list is refreshed via onSaved.
//   - instanceCount > 0 renders the tree READ-ONLY with no save
//     button at all: structure is immutable once audits reference
//     the version (server 409 template_in_use), so the UI never
//     offers a doomed save. The escape hatch is the same
//     create-new-version flow as the row action (onCreateNewVersion).
//   - Save errors surface BOTH the server's error code and its
//     detail string ("Section 2 Q 3 ...") - the detail is what
//     tells the operator which field to fix.
//   - Client-side pre-checks MIRROR server rules only (weightings
//     sum to 100 within 0.001; no em/en dashes in prose). The
//     server remains the gate for everything else.
//   - Ordering uses move up/down BUTTONS, never drag-and-drop
//     (house accessibility ruling). SortOrder = array order.
//   - QS mapping weights are not surfaced in v1; mappings are sent
//     as [{ qsCode }] in Quality Statement catalogue order.
//   - Conditional questions (2026-10-09): "Show this question only
//     when..." rules point at an EARLIER question. In state a rule holds
//     the parent's local key (so renaming a code or moving a question
//     keeps the link); it is sent as { sectionCode, questionCode,
//     operator, value }. A rule whose question is now later or removed
//     blocks the save with a plain message.
// ============================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import Modal from '../components/Modal';
import StatusMessage from '../components/StatusMessage';
import Field from '../components/Field';
import { toast } from '../components/Toaster';
import {
  getPlatformAuditTemplate,
  getPlatformQualityStatements,
  putPlatformAuditTemplateStructure,
} from '../api/adminPlatform';
import { translateError } from '../utils/errorMessages';
import './PlatformPages.css';

const SCORE_BANDS = ['0', '1', '2', '3'];
const MAX_RULES = 5;
// Kept in step with backend shared/audit-conditions.js OPERATORS.
export const RULE_OPERATORS = [
  { op: '<', label: 'is scored below' },
  { op: '<=', label: 'is scored at or below' },
  { op: '=', label: 'is scored exactly' },
  { op: '<>', label: 'is not scored' },
  { op: '>=', label: 'is scored at or above' },
  { op: '>', label: 'is scored above' },
  { op: 'na', label: 'is marked N/A' },
];
const OPERATOR_LABEL = Object.fromEntries(RULE_OPERATORS.map((o) => [o.op, o.label]));
const DOMAIN_LABELS = {
  safe: 'Safe', effective: 'Effective', caring: 'Caring',
  responsive: 'Responsive', well_led: 'Well-led',
};
// The server refuses em (U+2014) and en (U+2013) dashes in all
// template prose; mirror that so a doomed save is never offered.
// Built from char codes rather than literal characters, so repo-wide
// dash sweeps never flag this file.
const DASH_RE = new RegExp('[' + String.fromCharCode(0x2013, 0x2014) + ']');

// Local-only React keys for tree rows (backend ids are absent on
// newly-added rows, and rows keep identity across moves).
let localKeyCounter = 0;
const nextKey = () => { localKeyCounter += 1; return `atb-${localKeyCounter}`; };

function blankQuestion() {
  return {
    key: nextKey(),
    code: '',
    questionText: '',
    guidanceNotes: '',
    scores: { 0: '', 1: '', 2: '', 3: '' },
    isRequired: true,
    isNotApplicableAllowed: false,
    qsCodes: [],
    qsOpen: false,
    showWhen: [],
  };
}

function blankRule(parentKey) {
  return { key: nextKey(), parentKey, operator: '<', value: '2' };
}

function blankSection() {
  return {
    key: nextKey(),
    code: '',
    title: '',
    description: '',
    weightingPercent: '',
    questions: [blankQuestion()],
  };
}

// Server detail response -> editable local tree. Two passes: keys
// first, so a rule can be linked to its parent question's key.
export function toEditableSections(detail) {
  const keyByCode = new Map();
  const tree = (detail.sections || []).map((s) => ({
    key: nextKey(),
    code: s.code || '',
    title: s.title || '',
    description: s.description || '',
    weightingPercent: s.weightingPercent != null ? String(s.weightingPercent) : '',
    questions: (s.questions || []).map((q) => ({
      key: nextKey(),
      code: q.code || '',
      questionText: q.questionText || '',
      guidanceNotes: q.guidanceNotes || '',
      scores: SCORE_BANDS.reduce((acc, band) => {
        acc[band] = (q.scoreDescriptions && q.scoreDescriptions[band] != null)
          ? String(q.scoreDescriptions[band]) : '';
        return acc;
      }, {}),
      isRequired: q.isRequired == null ? true : Boolean(q.isRequired),
      isNotApplicableAllowed: Boolean(q.isNotApplicableAllowed),
      qsCodes: (q.qsMappings || []).map((m) => m.qsCode),
      qsOpen: false,
      rawShowWhen: q.showWhen || [],
    })),
  }));
  tree.forEach((s, si) => s.questions.forEach((q, qi) => {
    const src = detail.sections[si].questions[qi];
    keyByCode.set(`${detail.sections[si].code}/${src.code}`, q.key);
  }));
  for (const s of tree) {
    for (const q of s.questions) {
      q.showWhen = q.rawShowWhen.map((c) => ({
        key: nextKey(),
        parentKey: keyByCode.get(`${c.sectionCode}/${c.questionCode}`) || null,
        operator: c.operator,
        value: c.operator === 'na' ? '' : String(c.value),
      }));
      delete q.rawShowWhen;
    }
  }
  return tree;
}

// Every question in template order with its position, for rule pickers.
export function flattenQuestions(sections) {
  const out = [];
  sections.forEach((s, si) => s.questions.forEach((q, qi) => {
    out.push({ key: q.key, si, qi, sectionCode: s.code.trim(), questionCode: q.code.trim(), text: q.questionText });
  }));
  return out;
}

// Problems with rules (the save stays blocked until they are fixed).
export function ruleProblems(sections) {
  const flat = flattenQuestions(sections);
  const indexByKey = new Map(flat.map((f, i) => [f.key, i]));
  const problems = [];
  flat.forEach((f, i) => {
    const q = sections[f.si].questions[f.qi];
    (q.showWhen || []).forEach((r) => {
      const name = `Section ${f.si + 1} question ${f.qi + 1}`;
      const pi = r.parentKey == null ? undefined : indexByKey.get(r.parentKey);
      if (pi === undefined) problems.push(`${name}: a "show only when" rule points at a question that has been removed. Change or remove the rule.`);
      else if (pi >= i) problems.push(`${name}: a "show only when" rule must point at an earlier question. Change or remove the rule.`);
    });
  });
  return problems;
}

// Editable local tree -> the documented PUT wire shape. Empty
// optional strings (description, guidanceNotes) are OMITTED; score
// bands with empty inputs omit the key, and all four empty sends
// scoreDescriptions: null; qsMappings is omitted when empty and is
// serialised in QS catalogue order (codes the catalogue does not
// know keep their stored order, after the known ones).
export function serialiseSections(sections, qsOrderIndex) {
  const codeByKey = new Map(flattenQuestions(sections).map((f) => [f.key, f]));
  return sections.map((s) => {
    const out = {
      code: s.code.trim(),
      title: s.title.trim(),
      weightingPercent: Number(s.weightingPercent),
      questions: s.questions.map((q) => {
        const filledBands = SCORE_BANDS.filter((band) => (q.scores[band] || '').trim() !== '');
        const scoreDescriptions = filledBands.length === 0
          ? null
          : filledBands.reduce((acc, band) => { acc[band] = q.scores[band].trim(); return acc; }, {});
        const qOut = {
          code: q.code.trim(),
          questionText: q.questionText.trim(),
          scoreDescriptions,
          isRequired: Boolean(q.isRequired),
          isNotApplicableAllowed: Boolean(q.isNotApplicableAllowed),
        };
        if ((q.guidanceNotes || '').trim() !== '') qOut.guidanceNotes = q.guidanceNotes.trim();
        if ((q.showWhen || []).length > 0) {
          qOut.showWhen = q.showWhen.map((r) => {
            const parent = codeByKey.get(r.parentKey) || { sectionCode: '', questionCode: '' };
            return {
              sectionCode: parent.sectionCode,
              questionCode: parent.questionCode,
              operator: r.operator,
              ...(r.operator === 'na' ? {} : { value: Number(r.value) }),
            };
          });
        }
        if (q.qsCodes.length > 0) {
          const ordered = q.qsCodes.slice().sort((a, b) => {
            const ia = qsOrderIndex.has(a) ? qsOrderIndex.get(a) : Number.MAX_SAFE_INTEGER;
            const ib = qsOrderIndex.has(b) ? qsOrderIndex.get(b) : Number.MAX_SAFE_INTEGER;
            return ia - ib;
          });
          qOut.qsMappings = ordered.map((qsCode) => ({ qsCode }));
        }
        return qOut;
      }),
    };
    if ((s.description || '').trim() !== '') out.description = s.description.trim();
    return out;
  });
}

// Both the code and the detail must reach the operator: the code is
// what the docs/support know, the detail names the exact section and
// question at fault.
function describeSaveError(err) {
  const code = err && typeof err.message === 'string' ? err.message : '';
  const detail = err && typeof err.detail === 'string' ? err.detail.trim() : '';
  if (code && detail) return `${code}: ${detail}`;
  if (detail) return detail;
  return translateError(err);
}

function moveItem(arr, index, delta) {
  const next = arr.slice();
  const [item] = next.splice(index, 1);
  next.splice(index + delta, 0, item);
  return next;
}

function formatSum(sum) {
  // Trim float noise (59.999999) without hiding a real fraction.
  return String(Number(sum.toFixed(3)));
}

export default function AuditTemplateBuilder({
  isOpen,
  templateId,
  onClose,
  onSaved,
  onCreateNewVersion,
}) {
  const [detail, setDetail] = useState(null);
  const [sections, setSections] = useState([]);
  const [qsItems, setQsItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (templateId == null) return;
    setLoading(true); setLoadError(null); setSaveError(null);
    try {
      const [full, qs] = await Promise.all([
        getPlatformAuditTemplate(templateId),
        getPlatformQualityStatements(),
      ]);
      setDetail(full);
      setSections(toEditableSections(full));
      setQsItems(qs.items || []);
    } catch (err) {
      setLoadError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    if (isOpen && templateId != null) {
      load();
    } else {
      setDetail(null); setSections([]); setLoadError(null); setSaveError(null);
    }
  }, [isOpen, templateId, load]);

  const qsOrderIndex = useMemo(() => {
    const map = new Map();
    qsItems.forEach((qs, i) => map.set(qs.code, i));
    return map;
  }, [qsItems]);

  const qsGroups = useMemo(() => {
    const groups = [];
    const byDomain = new Map();
    for (const qs of qsItems) {
      if (!byDomain.has(qs.domain)) {
        const group = { domain: qs.domain, items: [] };
        byDomain.set(qs.domain, group);
        groups.push(group);
      }
      byDomain.get(qs.domain).items.push(qs);
    }
    return groups;
  }, [qsItems]);

  const readOnly = Boolean(detail && detail.instanceCount > 0);

  const weightSum = useMemo(
    () => sections.reduce((total, s) => total + (Number(s.weightingPercent) || 0), 0),
    [sections]
  );
  const weightOk = Math.abs(weightSum - 100) <= 0.001;

  // Client-side mirrors of server rules only (the server is the gate).
  const blockingProblems = useMemo(() => {
    const problems = [];
    if (sections.length > 0 && !weightOk) {
      problems.push(`Section weightings must sum to 100 (currently ${formatSum(weightSum)}).`);
    }
    sections.forEach((s, si) => {
      const sectionName = `Section ${si + 1}`;
      if (DASH_RE.test(s.title)) problems.push(`${sectionName} title contains an em or en dash. Replace it with a hyphen or reword.`);
      if (DASH_RE.test(s.description)) problems.push(`${sectionName} description contains an em or en dash. Replace it with a hyphen or reword.`);
      s.questions.forEach((q, qi) => {
        const questionName = `${sectionName} question ${qi + 1}`;
        if (DASH_RE.test(q.questionText)) problems.push(`${questionName} text contains an em or en dash. Replace it with a hyphen or reword.`);
        if (DASH_RE.test(q.guidanceNotes)) problems.push(`${questionName} guidance contains an em or en dash. Replace it with a hyphen or reword.`);
        SCORE_BANDS.forEach((band) => {
          if (DASH_RE.test(q.scores[band])) problems.push(`${questionName} score ${band} description contains an em or en dash. Replace it with a hyphen or reword.`);
        });
      });
    });
    problems.push(...ruleProblems(sections));
    return problems;
  }, [sections, weightOk, weightSum]);

  const flatQuestions = useMemo(() => flattenQuestions(sections), [sections]);
  const describeQuestion = (key) => {
    const f = flatQuestions.find((x) => x.key === key);
    if (!f) return 'a removed question';
    const text = f.text.length > 60 ? `${f.text.slice(0, 60)}...` : f.text;
    return `S${f.si + 1} Q${f.qi + 1}${text ? `: ${text}` : ''}`;
  };

  // ── Tree mutations (SortOrder = array order) ───────────────
  const updateSection = (key, patch) => {
    setSections((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  };
  const updateQuestion = (sectionKey, questionKey, patch) => {
    setSections((prev) => prev.map((s) => (s.key !== sectionKey ? s : {
      ...s,
      questions: s.questions.map((q) => (q.key === questionKey ? { ...q, ...patch } : q)),
    })));
  };
  const toggleQsCode = (sectionKey, questionKey, qsCode) => {
    setSections((prev) => prev.map((s) => (s.key !== sectionKey ? s : {
      ...s,
      questions: s.questions.map((q) => {
        if (q.key !== questionKey) return q;
        const has = q.qsCodes.includes(qsCode);
        return { ...q, qsCodes: has ? q.qsCodes.filter((c) => c !== qsCode) : [...q.qsCodes, qsCode] };
      }),
    })));
  };

  async function handleSave() {
    setSaving(true); setSaveError(null);
    try {
      const body = serialiseSections(sections, qsOrderIndex);
      const res = await putPlatformAuditTemplateStructure(templateId, body);
      const rules = res.conditionCount ? `, ${res.conditionCount} show-when rule${res.conditionCount === 1 ? '' : 's'}` : '';
      toast.success(`Structure saved: ${res.sectionCount} sections, ${res.questionCount} questions, ${res.mappingCount} QS mappings${rules}.`);
      if (onSaved) onSaved();
      await load();
    } catch (err) {
      setSaveError(describeSaveError(err));
    } finally {
      setSaving(false);
    }
  }

  const title = detail
    ? `Template builder: ${detail.title} v${detail.version}${detail.isActive ? '' : ' (draft)'}`
    : 'Template builder';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} closeDisabled={saving} size="wide">
      {loading && <StatusMessage as="p" variant="loading">Loading template structure…</StatusMessage>}
      {loadError && (
        <StatusMessage as="div" variant="error" className="platform-page__form-error">
          {loadError}{' '}
          <button type="button" className="platform-page__row-action" onClick={load}>Retry</button>
        </StatusMessage>
      )}

      {!loading && !loadError && detail && readOnly && (
        <>
          <StatusMessage as="p" variant="empty" className="platform-builder__readonly-note">
            Audits have been run against this version, so its content is their record and cannot change.
            Create a new version to edit.
          </StatusMessage>
          <div>
            <button
              type="button"
              className="platform-page__btn-primary"
              onClick={() => onCreateNewVersion(templateId)}
            >
              Create new version
            </button>
          </div>
          <div className="platform-builder__readonly-tree">
            {sections.map((s, si) => (
              <div key={s.key} className="platform-builder__section">
                <h4 className="platform-builder__readonly-heading">
                  {si + 1}. {s.title} ({s.weightingPercent}%){' '}
                  <code className="platform-page__code">{s.code}</code>
                </h4>
                {s.description && <p className="platform-builder__readonly-muted">{s.description}</p>}
                <ol className="platform-builder__readonly-questions">
                  {s.questions.map((q) => (
                    <li key={q.key}>
                      <div>{q.questionText}</div>
                      {q.guidanceNotes && <div className="platform-builder__readonly-muted">{q.guidanceNotes}</div>}
                      <div className="platform-builder__readonly-muted">
                        {q.isRequired ? 'Required' : 'Optional'}
                        {q.isNotApplicableAllowed ? ' · N/A allowed' : ''}
                        {q.qsCodes.length > 0 ? ` · QS: ${q.qsCodes.join(', ')}` : ''}
                      </div>
                      {(q.showWhen || []).length > 0 && (
                        <div className="platform-builder__readonly-muted">
                          Shown only when {q.showWhen.map((r) => `${describeQuestion(r.parentKey)} ${OPERATOR_LABEL[r.operator] || r.operator}${r.operator === 'na' ? '' : ` ${r.value}`}`).join(' and ')}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && !loadError && detail && !readOnly && (
        <div className="platform-builder">
          <p
            className={`platform-builder__weight-sum ${weightOk ? 'platform-builder__weight-sum--ok' : 'platform-builder__weight-sum--off'}`}
            data-testid="weight-sum"
          >
            Weightings: {formatSum(weightSum)} / 100
          </p>

          {sections.length === 0 && (
            <StatusMessage as="p" variant="empty">No sections yet. Add the first one below.</StatusMessage>
          )}

          {sections.map((s, si) => (
            <div key={s.key} className="platform-builder__section">
              <div className="platform-builder__section-head">
                <span className="platform-builder__ordinal">Section {si + 1}</span>
                <div className="platform-builder__move">
                  <button
                    type="button" className="platform-page__row-action"
                    aria-label={`Move section ${si + 1} up`}
                    disabled={si === 0}
                    onClick={() => setSections((prev) => moveItem(prev, si, -1))}
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button" className="platform-page__row-action"
                    aria-label={`Move section ${si + 1} down`}
                    disabled={si === sections.length - 1}
                    onClick={() => setSections((prev) => moveItem(prev, si, 1))}
                  >
                    <ArrowDown size={12} />
                  </button>
                  <button
                    type="button" className="platform-page__row-action"
                    aria-label={`Remove section ${si + 1}`}
                    onClick={() => setSections((prev) => prev.filter((x) => x.key !== s.key))}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              <div className="platform-builder__section-grid">
                <Field label="Code" help="Lower-snake-case, 3-100 chars.">
                  <input
                    type="text" value={s.code} maxLength={100}
                    aria-label={`Section ${si + 1} code`}
                    onChange={(e) => updateSection(s.key, { code: e.target.value })}
                  />
                </Field>
                <Field label="Title">
                  <input
                    type="text" value={s.title} maxLength={500}
                    aria-label={`Section ${si + 1} title`}
                    onChange={(e) => updateSection(s.key, { title: e.target.value })}
                  />
                </Field>
                <Field label="Weighting %">
                  <input
                    type="number" min={0} max={100} step="any" value={s.weightingPercent}
                    aria-label={`Section ${si + 1} weighting percent`}
                    onChange={(e) => updateSection(s.key, { weightingPercent: e.target.value })}
                  />
                </Field>
              </div>
              <Field label="Description">
                <textarea
                  rows={2} value={s.description} maxLength={4000}
                  aria-label={`Section ${si + 1} description`}
                  onChange={(e) => updateSection(s.key, { description: e.target.value })}
                />
              </Field>

              {s.questions.map((q, qi) => (
                <div key={q.key} className="platform-builder__question">
                  <div className="platform-builder__section-head">
                    <span className="platform-builder__ordinal">Question {qi + 1}</span>
                    <div className="platform-builder__move">
                      <button
                        type="button" className="platform-page__row-action"
                        aria-label={`Move section ${si + 1} question ${qi + 1} up`}
                        disabled={qi === 0}
                        onClick={() => updateSection(s.key, { questions: moveItem(s.questions, qi, -1) })}
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        type="button" className="platform-page__row-action"
                        aria-label={`Move section ${si + 1} question ${qi + 1} down`}
                        disabled={qi === s.questions.length - 1}
                        onClick={() => updateSection(s.key, { questions: moveItem(s.questions, qi, 1) })}
                      >
                        <ArrowDown size={12} />
                      </button>
                      <button
                        type="button" className="platform-page__row-action"
                        aria-label={`Remove section ${si + 1} question ${qi + 1}`}
                        onClick={() => updateSection(s.key, { questions: s.questions.filter((x) => x.key !== q.key) })}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>

                  <div className="platform-builder__question-grid">
                    <Field label="Code" help="Lower-snake-case, 3-100 chars. Unique across the template.">
                      <input
                        type="text" value={q.code} maxLength={100}
                        aria-label={`Section ${si + 1} question ${qi + 1} code`}
                        onChange={(e) => updateQuestion(s.key, q.key, { code: e.target.value })}
                      />
                    </Field>
                    <div className="platform-builder__flags">
                      <label className="platform-builder__flag">
                        <input
                          type="checkbox" checked={q.isRequired}
                          aria-label={`Section ${si + 1} question ${qi + 1} required`}
                          onChange={(e) => updateQuestion(s.key, q.key, { isRequired: e.target.checked })}
                        />
                        Required
                      </label>
                      <label className="platform-builder__flag">
                        <input
                          type="checkbox" checked={q.isNotApplicableAllowed}
                          aria-label={`Section ${si + 1} question ${qi + 1} allow not applicable`}
                          onChange={(e) => updateQuestion(s.key, q.key, { isNotApplicableAllowed: e.target.checked })}
                        />
                        Allow N/A
                      </label>
                    </div>
                  </div>
                  <Field label="Question text">
                    <textarea
                      rows={2} value={q.questionText}
                      aria-label={`Section ${si + 1} question ${qi + 1} text`}
                      onChange={(e) => updateQuestion(s.key, q.key, { questionText: e.target.value })}
                    />
                  </Field>
                  <Field label="Guidance notes">
                    <textarea
                      rows={2} value={q.guidanceNotes}
                      aria-label={`Section ${si + 1} question ${qi + 1} guidance`}
                      onChange={(e) => updateQuestion(s.key, q.key, { guidanceNotes: e.target.value })}
                    />
                  </Field>

                  <fieldset className="platform-builder__scores">
                    <legend className="platform-builder__scores-legend">Score band descriptions (0 to 3, optional)</legend>
                    <div className="platform-builder__scores-grid">
                      {SCORE_BANDS.map((band) => (
                        <Field key={band} label={`Score ${band}`}>
                          <input
                            type="text" value={q.scores[band]}
                            aria-label={`Section ${si + 1} question ${qi + 1} score ${band} description`}
                            onChange={(e) => updateQuestion(s.key, q.key, {
                              scores: { ...q.scores, [band]: e.target.value },
                            })}
                          />
                        </Field>
                      ))}
                    </div>
                  </fieldset>

                  <fieldset className="platform-builder__rules">
                    <legend className="platform-builder__scores-legend">Show this question only when (optional)</legend>
                    {(q.showWhen || []).length === 0 && (
                      <p className="platform-builder__readonly-muted">Always shown.</p>
                    )}
                    {(q.showWhen || []).map((r, ri) => {
                      const myIndex = flatQuestions.findIndex((f) => f.key === q.key);
                      const earlier = flatQuestions.slice(0, Math.max(0, myIndex));
                      const ruleName = `Section ${si + 1} question ${qi + 1} rule ${ri + 1}`;
                      const patchRule = (patch) => updateQuestion(s.key, q.key, {
                        showWhen: q.showWhen.map((x) => (x.key === r.key ? { ...x, ...patch } : x)),
                      });
                      return (
                        <div key={r.key} className="platform-builder__rule">
                          {ri > 0 && <span className="platform-builder__rule-and">and</span>}
                          <select
                            value={r.parentKey || ''}
                            aria-label={`${ruleName} question`}
                            onChange={(e) => patchRule({ parentKey: e.target.value || null })}
                          >
                            {!earlier.some((f) => f.key === r.parentKey) && (
                              <option value={r.parentKey || ''}>{describeQuestion(r.parentKey)}</option>
                            )}
                            {earlier.map((f) => (
                              <option key={f.key} value={f.key}>{describeQuestion(f.key)}</option>
                            ))}
                          </select>
                          <select
                            value={r.operator}
                            aria-label={`${ruleName} condition`}
                            onChange={(e) => patchRule({ operator: e.target.value, value: e.target.value === 'na' ? '' : (r.value || '2') })}
                          >
                            {RULE_OPERATORS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
                          </select>
                          {r.operator !== 'na' && (
                            <select
                              value={r.value}
                              aria-label={`${ruleName} score`}
                              onChange={(e) => patchRule({ value: e.target.value })}
                            >
                              {SCORE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                          )}
                          <button
                            type="button" className="platform-page__row-action"
                            aria-label={`Remove ${ruleName}`}
                            onClick={() => updateQuestion(s.key, q.key, { showWhen: q.showWhen.filter((x) => x.key !== r.key) })}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      );
                    })}
                    {(() => {
                      const myIndex = flatQuestions.findIndex((f) => f.key === q.key);
                      const firstEarlier = myIndex > 0 ? flatQuestions[myIndex - 1].key : null;
                      const atMax = (q.showWhen || []).length >= MAX_RULES;
                      return (
                        <div>
                          <button
                            type="button" className="platform-page__row-action"
                            disabled={!firstEarlier || atMax}
                            aria-label={`Section ${si + 1} question ${qi + 1} add show-when rule`}
                            onClick={() => updateQuestion(s.key, q.key, { showWhen: [...(q.showWhen || []), blankRule(firstEarlier)] })}
                          >
                            <Plus size={12} /> Add a rule
                          </button>
                          {!firstEarlier && (
                            <span className="platform-builder__readonly-muted"> The first question cannot depend on another.</span>
                          )}
                          {atMax && (
                            <span className="platform-builder__readonly-muted"> Up to {MAX_RULES} rules per question.</span>
                          )}
                        </div>
                      );
                    })()}
                  </fieldset>

                  <div className="platform-builder__qs">
                    <button
                      type="button"
                      className="platform-page__row-action"
                      aria-expanded={q.qsOpen}
                      aria-label={`Section ${si + 1} question ${qi + 1} quality statements`}
                      onClick={() => updateQuestion(s.key, q.key, { qsOpen: !q.qsOpen })}
                    >
                      Quality statements ({q.qsCodes.length})
                    </button>
                    {q.qsOpen && (
                      <div className="platform-builder__qs-groups">
                        {qsGroups.length === 0 && (
                          <StatusMessage as="p" variant="empty">No quality statements available.</StatusMessage>
                        )}
                        {qsGroups.map((group) => (
                          <div key={group.domain} className="platform-builder__qs-group">
                            <span className="platform-builder__qs-domain">{DOMAIN_LABELS[group.domain] || group.domain}</span>
                            {group.items.map((qs) => (
                              <label key={qs.code} className="platform-builder__flag">
                                <input
                                  type="checkbox"
                                  checked={q.qsCodes.includes(qs.code)}
                                  onChange={() => toggleQsCode(s.key, q.key, qs.code)}
                                />
                                <code className="platform-page__code">{qs.code}</code> {qs.title}
                              </label>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div>
                <button
                  type="button" className="platform-page__row-action"
                  onClick={() => updateSection(s.key, { questions: [...s.questions, blankQuestion()] })}
                >
                  <Plus size={12} /> Add question
                </button>
              </div>
            </div>
          ))}

          <div>
            <button
              type="button" className="platform-page__row-action"
              onClick={() => setSections((prev) => [...prev, blankSection()])}
            >
              <Plus size={12} /> Add section
            </button>
          </div>

          {blockingProblems.length > 0 && (
            <ul className="platform-builder__problems">
              {blockingProblems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          {saveError && (
            <StatusMessage as="p" variant="error" className="platform-page__form-error">{saveError}</StatusMessage>
          )}

          <div className="platform-page__form-actions">
            <button type="button" className="platform-page__btn-secondary" onClick={onClose} disabled={saving}>
              Close
            </button>
            <button
              type="button"
              className="platform-page__btn-primary"
              onClick={handleSave}
              disabled={saving || blockingProblems.length > 0}
            >
              {saving ? 'Saving…' : 'Save structure'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
